import * as React from 'react';
import { insertUrlParam } from '../../../utilities/browser';
import { isIdenticalObject } from '../../../utilities/objects';
import SvgImage from '../SvgImage';
import './styles.scss';

interface PaginationProps {
  totalItems: number;
  currentPage: number;
  itemsPerPage: number;
  handleClick?: Function;
  pageQueryKey?: string;
  retainRoute?: boolean;
  style?: string;
}

interface PaginationState {
  pages: number[];
  firstPageIndex: number | undefined;
  lastPageIndex: number | undefined;
  previousPageIndex: number | undefined;
  nextPageIndex: number | undefined;
  totalItems?: number | undefined;
  currentPage: number | undefined;
  totalAmountOfPages: number | undefined;
  isFirstPageDisabled: boolean | undefined;
  isLastPageDisabled: boolean | undefined;
  isPreviousDisabled: boolean | undefined;
  isNextDisabled: boolean | undefined;
  // The below need to move to props with defaults
  maxVisiblePagesMobile: number;
  maxVisiblePagesDesktop: number;
  maxVisiblePages: number;
}

// move to prop with default value
const breakpoint = 620;

const defaultProps = {
  pageQueryKey: 'page',
};
export default class Pagination extends React.Component<PaginationProps, PaginationState> {
  // eslint-disable-next-line react/static-property-placement
  static defaultProps = defaultProps;

  constructor(props: PaginationProps) {
    super(props);
    this.state = {
      pages: [],
      firstPageIndex: undefined,
      lastPageIndex: undefined,
      previousPageIndex: undefined,
      nextPageIndex: undefined,
      // totalItems: undefined,
      currentPage: undefined,
      isFirstPageDisabled: undefined,
      isLastPageDisabled: undefined,
      totalAmountOfPages: undefined,
      isPreviousDisabled: undefined,
      isNextDisabled: undefined,
      maxVisiblePagesMobile: 3,
      maxVisiblePagesDesktop: 5,
      maxVisiblePages: 5,
    };
  }

  async componentDidMount() {
    await this.handdleMaxVisiblePages(window.innerWidth);
    window.addEventListener('resize', async (event) => {
      event.stopPropagation();
      await this.handdleMaxVisiblePages(window.innerWidth);
    });

    this.getPager();
  }

  componentDidUpdate(prevProps: PaginationProps) {
    const hasPropsUpdated = isIdenticalObject(prevProps, this.props);
    if (!hasPropsUpdated) {
      this.getPager();
    }
  }

  /**
   * This handles setting the state of the pager.
   * Flags toggle display views on pager elements
   * and fetches and sets the visable page list
   * from generateVisablePages.
   */
  getPager() {
    const {
      totalItems,
      currentPage,
      itemsPerPage,
    } = this.props;

    const {
      maxVisiblePages,
    } = this.state;

    const totalAmountOfPages = Math.ceil(totalItems / itemsPerPage);
    const isFirstPageDisabled = (
      (!(currentPage > (Math.ceil(maxVisiblePages / 2)))) || totalAmountOfPages <= maxVisiblePages
    );
    const isLastPageDisabled = currentPage > totalAmountOfPages - Math.ceil(maxVisiblePages / 2)
      || totalAmountOfPages <= maxVisiblePages;
    const lastPageIndex = totalAmountOfPages;
    const previousPageIndex = (currentPage - 1);
    const isPreviousDisabled = !(currentPage - 1);
    const isNextDisabled = (currentPage + 1) > lastPageIndex;

    this.setState({
      pages: this.generateVisablePages(
        totalAmountOfPages,
        currentPage,
      ),
      firstPageIndex: 1,
      lastPageIndex,
      previousPageIndex,
      nextPageIndex: (currentPage + 1),
      // totalItems,
      currentPage,
      totalAmountOfPages,
      isFirstPageDisabled,
      isLastPageDisabled,
      isPreviousDisabled,
      isNextDisabled,
    });
  }

  /**
   * Generates and returns the list of visible
   * pages based on the current page and the
   * maxVisiblePages.
   * @param totalAmountOfPages
   * @param currentPage
   */
  generateVisablePages(totalAmountOfPages: number, currentPage: number) {
    const { maxVisiblePages } = this.state;
    const maxVisibleOffset = (maxVisiblePages % 2 === 0) ? 1 : 0.5;
    let startPage = 1;
    let endPage = (maxVisiblePages + 1);

    if (currentPage > 2) {
      startPage = (currentPage - (maxVisiblePages / 2 - maxVisibleOffset));
      endPage = (currentPage + (maxVisiblePages / 2 + maxVisibleOffset));
    }

    if (currentPage > (totalAmountOfPages - 2)) {
      startPage = (totalAmountOfPages - (maxVisiblePages - 1));
      endPage = totalAmountOfPages + 1;
    }

    if (totalAmountOfPages <= maxVisiblePages) {
      startPage = 1;
      endPage = totalAmountOfPages + 1;
    }

    return Array.from({
      length: (endPage - startPage),
    }, (s, l) => l + startPage);
  }

  /**
   * Checks the viewport width to determine the
   * maxVisiblePages based on mobile/desktop
   * @param viewportWidth
   */
  async handdleMaxVisiblePages(viewportWidth: number) {
    const {
      maxVisiblePagesMobile,
      maxVisiblePagesDesktop,
      totalAmountOfPages,
      currentPage,
    } = this.state;

    if (viewportWidth < breakpoint) {
      await this.setState({
        maxVisiblePages: maxVisiblePagesMobile,
      });
    } else {
      await this.setState({
        maxVisiblePages: maxVisiblePagesDesktop,
      });
    }

    this.setState({
      pages: this.generateVisablePages(
        totalAmountOfPages as number,
        currentPage as number,
      ),
    });
  }

  /**
   * Change page state handler
   * @param event
   * @param page
   */
  changePage(event: React.MouseEvent, page: number) {
    const {
      pageQueryKey,
      retainRoute,
      handleClick,
    } = this.props;

    event.preventDefault();
    insertUrlParam((pageQueryKey || ''), String(page), retainRoute);
    if (handleClick) {
      handleClick(page);
    }
  }

  /**
   * Generates the page buttons
   * @param page
   * @param text
   * @param isDisabled
   */
  generateButton(page: number, text: string, isDisabled: boolean) {
    const { retainRoute } = this.props;
    const rootLocation = retainRoute ? '' : `${window.location.pathname}?page=${page}`;
    return (
      <a
        className={`pager__item-link button ${isDisabled ? 'button--disabled' : ''}`}
        onClick={(event) => (!isDisabled ? this.changePage(event, page) : null)}
        href={rootLocation}
        role="button"
      >
        <span>{text}</span>
      </a>
    );
  }

  render() {
    const {
      style,
    } = this.props;

    const {
      pages,
      firstPageIndex,
      lastPageIndex,
      previousPageIndex,
      nextPageIndex,
      currentPage,
      isFirstPageDisabled,
      isLastPageDisabled,
      isPreviousDisabled,
      isNextDisabled,
      totalAmountOfPages,
    } = this.state;

    return (
      <div className={`pagination ${style && `pagination--${style}`}`}>
        <ul className="pager">

          {/* First page */}
          <li className={`pager__item pager__item--first-page ${isFirstPageDisabled && 'pager__item--disabled'}`}>
            {this.generateButton(firstPageIndex as number, '1', isFirstPageDisabled as boolean)}
          </li>

          {/* Page list */}
          {pages.map((item) => (
            <li key={item} className={`pager__item ${(item === currentPage) ? 'pager__item--active' : ''}`}>
              {this.generateButton(item, String(item), false)}
            </li>
          ))}

          {/* Last page */}
          <li className={`pager__item pager__item--last-page ${isLastPageDisabled && 'pager__item--disabled'}`}>
            {this.generateButton(lastPageIndex as number, `${totalAmountOfPages}`, isLastPageDisabled as boolean)}
          </li>
        </ul>

        <div className="pagination__paddles">
          {/* Previous page */}
          <div className={`previous-page ${isPreviousDisabled && 'previous-page--disabled'}`}>
            <SvgImage title="previous arrow" reference="#icon-right-paddle" />
            <a href="#" onClick={(event) => (!isPreviousDisabled ? this.changePage(event, previousPageIndex as number) : () => {})}>
              <span>Previous page</span>
            </a>
          </div>

          {/* Next page */}
          <div className={`next-page ${isNextDisabled && 'next-page--disabled'}`}>
            <SvgImage title="next arrow" reference="#icon-right-paddle" />
            <a href="#" onClick={(event) => (!isNextDisabled ? this.changePage(event, nextPageIndex as number) : () => {})}>
              <span>Next page</span>
            </a>
          </div>
        </div>

      </div>

    );
  }
}
